import React, { useEffect, useState } from 'react';
import {
	Alert,
	Box,
	CircularProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { Air } from 'features/quotation/types';
import { FlightsTableDesktop } from 'features/quotation/components/AirConfig/FlightsTableDesktop';
import { FlightMobile } from 'features/quotation/components/AirConfig/FlightMobile';
import FlightImagePreviewList from 'features/quotation/components/AirConfig/FileImagePreviewList';
import useBreakpoints from 'features/common/hooks/useBreakpoints';
import { parseGDSAirRawDataSaleOrder } from 'features/salesOrder/services';
import { IFlightServiceLeg } from 'features/salesOrder/types';

interface Props {
	air: Air;
}

export const FlightItineraryPreview = ({ air }: Props): JSX.Element | null => {
	const { mediumDown } = useBreakpoints();
	const hasData = Boolean(air.data?.length);
	const hasImages = Boolean(air.images?.length);
	const hasTst = Boolean(air.tst?.trim());

	const [tstLegs, setTstLegs] = useState<IFlightServiceLeg[] | null>(null);
	const [tstLoading, setTstLoading] = useState(false);
	const [tstError, setTstError] = useState(false);

	useEffect(() => {
		if (hasData || !hasTst) {
			setTstLegs(null);
			setTstError(false);
			return;
		}

		let active = true;
		setTstLoading(true);
		setTstError(false);

		void parseGDSAirRawDataSaleOrder(air.tst)
			.then((res) => {
				if (active) {
					setTstLegs(res.data || []);
				}
			})
			.catch(() => {
				if (active) {
					setTstError(true);
					setTstLegs([]);
				}
			})
			.finally(() => {
				if (active) {
					setTstLoading(false);
				}
			});

		return () => {
			active = false;
		};
	}, [air.tst, hasData, hasTst]);

	if (!hasData && !hasImages && !hasTst) {
		return (
			<Typography variant="body2" color="text.secondary">
				Sin detalle de itinerario en esta opción.
			</Typography>
		);
	}

	return (
		<Box>
			<Typography variant="subtitle2" fontWeight={600} gutterBottom>
				Itinerario
			</Typography>

			{hasData && (
				<Paper variant="outlined" sx={{ overflow: 'auto', mb: hasImages ? 2 : 0 }}>
					{mediumDown ? (
						<FlightMobile flightInfo={air.data || []} />
					) : (
						<FlightsTableDesktop flightInfo={air.data || []} />
					)}
				</Paper>
			)}

			{!hasData && hasTst && (
				<Box sx={{ mb: hasImages ? 2 : 0 }}>
					{tstLoading && (
						<Box display="flex" alignItems="center" gap={1}>
							<CircularProgress size={20} />
							<Typography variant="body2">Interpretando TST…</Typography>
						</Box>
					)}
					{tstError && (
						<Alert severity="warning" sx={{ mb: 1 }}>
							No se pudo leer el TST para mostrar el itinerario.
						</Alert>
					)}
					{!tstLoading && tstLegs && tstLegs.length > 0 && (
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Vuelo</TableCell>
									<TableCell>Salida</TableCell>
									<TableCell>Llegada</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{tstLegs.map((leg) => (
									<TableRow key={leg.keyId}>
										<TableCell>
											{leg.airline.code} {leg.flightNumber}
										</TableCell>
										<TableCell>
											{leg.departure.airport.code} {leg.departure.date} {leg.departure.hour}
										</TableCell>
										<TableCell>
											{leg.arrival.airport.code} {leg.arrival.date} {leg.arrival.hour}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</Box>
			)}

			{hasImages && <FlightImagePreviewList flightImages={air.images} />}
		</Box>
	);
};
